/**
 * The one-letter field names used throughout the BUDs.
 *
 * The manifest format is terse on purpose — these names are what actually goes
 * on the wire, and every byte of a key name is a byte in every node of every
 * tree. That terseness is hostile to a reader meeting them for the first time,
 * so every occurrence in the UI is an `<abbr>` carrying the human name.
 *
 * Defined once here so a field cannot end up described two different ways on
 * two different slides.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Field names from the BUD-16 node and link maps, plus the nostr tags we use. */
export const PROP_NAMES: Record<string, string> = {
  // BUD-16 node fields
  l: "links — the array of child links",
  t: "type — the node or link type",
  // BUD-16 link fields
  h: "hash — the 32 byte Blossom hash of the linked object",
  k: "key — the 32 byte client-side decryption key, a bearer secret",
  m: "metadata — a JSON-compatible map",
  n: "name — the directory entry name",
  s: "size — plaintext bytes represented by this link",
  // BUD-17 fanout metadata
  count: "count — user-visible entries in this subtree",
  first: "first — the first entry name in this subtree, after sorting",
  last: "last — the last entry name in this subtree, after sorting",
  // nostr tags
  d: "d — the replaceable event identifier, here the tree name",
  x: "x — the blob hash this authorization is bound to",
};

/** What each `t` value points at. */
export const TYPE_NAMES: Record<number, string> = {
  0: "raw blob, which has no node type of its own",
  1: "file manifest (BUD-17)",
  2: "directory manifest (BUD-16)",
  3: "directory fanout node (BUD-17)",
};

const ABBR_CLASS =
  "font-mono decoration-dotted underline-offset-2 decoration-muted-foreground/60 [text-decoration-line:underline] cursor-help";

/**
 * A single-letter field name with its meaning attached.
 *
 * Pass `value` for the `t=2` form, which reads as one token and so gets one
 * tooltip naming both the field and that specific type.
 */
export function Prop({
  name,
  value,
  className,
}: {
  name: string;
  value?: number | string;
  className?: string;
}) {
  const meaning = PROP_NAMES[name] ?? name;

  const title =
    value !== undefined && name === "t" && typeof value === "number" && TYPE_NAMES[value]
      ? `${meaning} — ${value} is a ${TYPE_NAMES[value]}`
      : meaning;

  return (
    <abbr title={title} className={cn(ABBR_CLASS, className)}>
      {value === undefined ? name : `${name}=${value}`}
    </abbr>
  );
}

/** The same thing as a `Field` label, where it sits beside the value. */
export function PropLabel({ name, hint }: { name: string; hint?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Prop name={name} />
      {hint}
    </span>
  );
}
